import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Building, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'account' | 'establishment'>('account');
  
  // Account form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Establishment form
  const [establishmentName, setEstablishmentName] = useState("");
  const [slug, setSlug] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleNameChange = (name: string) => {
    setEstablishmentName(name);
    setSlug(generateSlug(name));
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères.",
        variant: "destructive",
      });
      return;
    }

    setStep('establishment');
  };

  const handleEstablishmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!establishmentName.trim() || !slug.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs.",
        variant: "destructive",
      });
      return;
    }

    if (!termsAccepted) {
      toast({
        title: "Erreur",
        description: "Veuillez accepter les conditions générales d'utilisation.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Create auth user + establishment atomically via edge function.
      // Client signUp cannot be used when Supabase "Confirm email" is ON
      // (no session is returned, so create-establishment cannot be authorized via JWT).
      const { data, error } = await supabase.functions.invoke('create-establishment', {
        body: {
          establishmentName: establishmentName.trim(),
          slug: slug.trim(),
          email: email.trim(),
          password,
          termsAccepted: true,
          termsVersion: '2025-01-10',
        }
      });

      if (data?.error) throw new Error(data.error);

      if (error) {
        // FunctionsHttpError: le corps JSON de la fonction est dans error.context (Response)
        let message = error.message;
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          }
        } catch {
          // garder message générique
        }
        throw new Error(message);
      }

      const userId = data?.userId as string | undefined;
      if (!userId) {
        throw new Error("Erreur lors de la création du compte");
      }

      // Envoyer le code de vérification personnalisé via noreply@piktable.ch
      await supabase.functions.invoke('send-verification-code', {
        body: {
          userId,
          email: email.trim(),
        }
      });

      toast({
        title: "Compte créé avec succès !",
        description: "Veuillez vérifier votre email avec le code envoyé.",
      });

      // Rediriger vers la page de vérification email
      // password is kept in memory (router state) to sign in after verification
      navigate('/verify-email', {
        state: {
          email: email.trim(),
          userId,
          password,
        }
      });
    } catch (error: any) {
      console.error('Error during signup:', error);
      
      let errorMessage = "Impossible de créer le compte.";
      if (error.message?.includes('duplicate') || error.message?.includes('existe déjà')) {
        errorMessage = error.message.includes('existe déjà')
          ? error.message
          : "Cet email ou ce slug est déjà utilisé.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <div className="container mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mt-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au site
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-serif">
            {step === 'account' ? 'Créer un compte' : 'Votre établissement'}
          </CardTitle>
          <CardDescription>
            {step === 'account' 
              ? 'Commencez à gérer vos réservations en quelques minutes'
              : 'Configurez votre établissement'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'account' ? (
            <form onSubmit={handleAccountSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full">
                Continuer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Déjà un compte ?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Se connecter
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleEstablishmentSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="establishmentName">Nom de l'établissement</Label>
                <Input
                  id="establishmentName"
                  value={establishmentName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Mon Restaurant"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL de réservation</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">piktable.ch/</span>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="mon-restaurant"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce sera l'URL que vos clients utiliseront pour réserver.
                </p>
              </div>
              <div className="flex items-start space-x-3 pt-2 pb-2">
                <Checkbox 
                  id="terms" 
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                />
                <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  J'accepte les{" "}
                  <Link 
                    to="/legal#cgu" 
                    target="_blank" 
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    conditions générales d'utilisation
                  </Link>
                  {" "}et la{" "}
                  <Link 
                    to="/legal#privacy" 
                    target="_blank" 
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    politique de confidentialité
                  </Link>
                </Label>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setStep('account')}
                  className="flex-1"
                >
                  Retour
                </Button>
                <Button type="submit" disabled={loading || !termsAccepted} className="flex-1">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Créer mon établissement"
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
